import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { IconButton, Popover, TextField, Tooltip, Box, Typography } from "@mui/material";
import { AddCircleOutline, ChatBubble, ChatBubbleOutline, Edit } from "@mui/icons-material";
import { useAreaActions } from "../hooks/useAreaActions";
import { colors } from "../../../../../../../ui/colors";

//JOBS BUBBLE
export const TaskNoteBubble = ({
  areaUid,
  taskId,
  taskName,
  noteFromStore = "",
  icon,
}: {
  areaUid: string;
  taskId: string;
  taskName: string;
  noteFromStore?: string;
  icon?: ReactNode;
}) => {
  const { setTaskNote } = useAreaActions();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const MAX_LENGTH = 100;

  const noteRef = useRef<string>(noteFromStore || "");

  const [len, setLen] = useState(noteRef.current.length);

  useEffect(() => {
    noteRef.current = noteFromStore || "";
    setLen(noteRef.current.length);
  }, [noteFromStore]);

  const open = Boolean(anchorEl);
  const id = open ? `note-popover-${taskId}` : undefined;

  const hasNote = (noteFromStore || "").trim().length > 0;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (e?: any) => {
    e?.stopPropagation?.();

    const finalValue = noteRef.current.slice(0, MAX_LENGTH);
    setTaskNote(areaUid, taskId, finalValue);

    setAnchorEl(null);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, MAX_LENGTH);
    noteRef.current = value;
    setLen(value.length);
  };

  const inputKey = useMemo(() => `${taskId}:${open ? "open" : "closed"}:${noteFromStore ?? ""}`, [taskId, open, noteFromStore]);

  return (
    <>
      <Tooltip title={hasNote ? "Edit note" : "Add note"}>
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{
            color: hasNote ? colors.orange.base : colors.gray[300],
            "&:hover": { color: colors.orange[500] },
            mr: 0.5,
            zIndex: 9999,
            position: "relative",
          }}
        >
          {icon ?? (hasNote ? <ChatBubble fontSize="small" /> : <ChatBubbleOutline fontSize="small" />)}
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1.5,
            width: 260,
            borderRadius: 2,
            zIndex: 99999,
          },
        }}
        sx={{ zIndex: 99999 }}
      >
        <Box onClick={(e) => e.stopPropagation()}>
          <TextField
            key={inputKey}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();
                handleClose(event);
              }

              if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                setAnchorEl(null);
              }
            }}
            multiline
            rows={3}
            fullWidth
            placeholder={`Write a note for task: ${taskName || "Service"}...`}
            defaultValue={noteFromStore || ""}
            onChange={handleInput}
            size="small"
            inputProps={{ maxLength: MAX_LENGTH }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: colors.gray[200] },
                "&:hover fieldset": { borderColor: colors.orange[400] },
                "&.Mui-focused fieldset": { borderColor: colors.orange.base },
              },
              "& .MuiInputBase-input": { fontSize: 13 },
            }}
          />

          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "right",
              mt: 0.5,
              color: len >= MAX_LENGTH ? colors.orange.base : colors.gray[400],
            }}
          >
            {len}/{MAX_LENGTH}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};
